import { HttpErrorResponse } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { OwnedPartner, ownedPartnerKind } from '../../models/partners.model';
import { AuthService } from '../../service/auth.service';
import { PartnersService } from '../../service/partners.service';
import { QR_LOGIN_ENABLED } from '../../web-auth/web-auth.flags';
import { decodeJwtClaims } from '../../web-auth/wl-codes';
import { WebSession, WebSessionRegistry } from '../../web-auth/web-session.registry';
import { WebTokenBridge } from '../../web-auth/web-token.bridge';
import { parseOrgError } from '../models/org-errors';

/**
 * Oturum yoksa ya da token scp'si 'org' değilse /org/login (hedef ?next ile korunur).
 *
 * Ent oturumu (panel QR, scp=ent; L4/F1): org oturumu yokken ent oturumunun kurumu Üyelik (Organizasyon/Partner) ise
 * sayfalar ikinci bir QR girişi istemeden ent token'ıyla açılır (sunucu /v2/Org/* uçlarını scp=ent'e açar). Kurum Şirket
 * ise, türü bilinmiyorsa ya da oturumda kurum anahtarı yoksa eskisi gibi /org/login. Org oturumu varsa ent yoluna girilmez.
 *
 * Döngü önlemi: login sayfası bellekteki oturumu `resume()` ile bulup /org'a geri yollar. Bu yüzden
 *  - kapsam/token uyumsuzluğunda oturum AÇIKÇA bitirilir (login'de bulunacak oturum kalmaz);
 *  - ağ/5xx hatasında oturum bellekte kalır, login'e `resume=0` ile gidilir: sayfa otomatik oturum açmaz,
 *    kullanıcı QR ile girer ya da bağlantı gelince sayfayı yeniler.
 */
export const orgSessionGuard: CanActivateFn = async (_route, state) => {
    const reg = inject(WebSessionRegistry);
    const router = inject(Router);
    const injector = inject(Injector);
    const toLogin = (extra: Record<string, string> = {}) =>
        router.createUrlTree(['/org/login'], { queryParams: { next: state.url, ...extra } });
    try {
        const session = reg.current('org') ?? (await reg.resume('org'));
        if (!session) {
            const ent = reg.current('ent') ?? (await reg.resume('ent'));
            return ent && (await entOpensOrg(reg, injector, ent)) ? true : toLogin();
        }
        const token = await reg.ensureFresh('org');
        if (token && decodeJwtClaims(token)?.scp === 'org') return true;
        await reg.end('org', 'expired');
        return toLogin();
    } catch (e) {
        // Yenileme 401 verdiyse registry oturumu zaten bitirdi: normal giriş. Aksi (ağ/5xx) halde döngü kırılır.
        // ENT2: kodsuz 401 oturumu bitirmez (registry yalnız DeviceRevoked/TokenExpired'da bitirir); ağ hatası gibi ele alınır.
        if (e instanceof HttpErrorResponse && e.status === 401 && ['DeviceRevoked', 'TokenExpired'].includes(parseOrgError(e).code)) return toLogin();
        console.warn('[org-guard] oturum doğrulanamadı', e);
        return toLogin({ resume: '0' });
    }
};

/**
 * Şirket "Yetkililer" sayfası (/pages/company-holders, entRouteGuard'ın arkasında): /org guard'ı genişletilmez, Üyelik ekranları
 * şirkete açılmaz. Keycloak oturumu önce QR girişine taşınır (/tasi). Üyelik kurumunun ent oturumu aynı işi /org/settings'te yapar.
 * Org isteklerini orgBacking taşır: başka kurumun org oturumu açıksa istekler ona gideceği için sayfa açılmaz; kurum anahtarı olmayan
 * ent oturumunda da açılmaz (interceptor org yüzeyine düşüp /org/login'e yönlenirdi).
 */
export const companyHoldersGuard: CanActivateFn = async () => {
    const reg = inject(WebSessionRegistry);
    const router = inject(Router);
    const injector = inject(Injector);
    const home = router.parseUrl('/');
    const auth = injector.get(AuthService);
    await auth.whenSessionReady();
    if (!injector.get(WebTokenBridge).isEnt()) return QR_LOGIN_ENABLED ? router.parseUrl('/tasi') : home;
    const ent = reg.current('ent');
    if (!ent?.orgId) return home;
    const org = reg.current('org');
    if (org && org.orgId?.toLowerCase() !== ent.orgId.toLowerCase()) return home;
    try {
        const p = await entOwnedPartner(reg, injector, ent);
        if (!p) return home;
        return ownedPartnerKind(p) === 'Membership' ? router.parseUrl('/org/settings') : true;
    } catch (e) {
        console.warn('[company-holders] kurum okunamadı', e);
        return home;
    }
};

// Ent oturumu /org/* sayfalarını açabilir mi: oturumun kurumu Owned'da Üyelik türünde. Ağ hatası çağırana atılır (guard resume=0 ile login'e gider).
async function entOpensOrg(reg: WebSessionRegistry, injector: Injector, ent: WebSession): Promise<boolean> {
    const p = await entOwnedPartner(reg, injector, ent);
    // Ent'te türü bilinmeyen satır Şirket sayılır (AccountModeService ile aynı kural).
    return !!p && ownedPartnerKind(p) === 'Membership';
}

// Ent oturumunun kurumu (org claim = Partner.Id) Owned'dan: kurum anahtarı var, token scp=ent, sekmenin panel yüzeyi ent (ya da boş: benimsenir).
async function entOwnedPartner(reg: WebSessionRegistry, injector: Injector, ent: WebSession): Promise<OwnedPartner | null> {
    if (!ent.orgId || ent.keys.length === 0) return null;
    const token = await reg.ensureFresh('ent');
    if (!token || decodeJwtClaims(token)?.scp !== 'ent') return null;
    // Owned isteği panelin token zincirinden (AuthService → WebTokenBridge) geçer: sekmede ent benimsenmiş olmalı.
    const auth = injector.get(AuthService);
    await auth.whenSessionReady();
    const bridge = injector.get(WebTokenBridge);
    if (!bridge.active()) auth.useWebSession(ent);
    else if (!bridge.isEnt()) return null; // sekmede admin oturumu: ent'e geçilmez
    const owned = await firstValueFrom(injector.get(PartnersService).owned());
    const orgId = ent.orgId.toLowerCase();
    return owned.find((x) => x.id?.toLowerCase() === orgId) ?? null;
}
